import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  useCallback,
} from "react";
import {
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Select,
  MenuItem,
  Box,
  Typography,
  FormControl,
  InputLabel,
} from "@mui/material";

import { decimalHoursToHHMM, hhmmToDecimalHours } from "../../utils/dateUtils";

const initialPayoutsState = {
  trip_payouts_kms_used: "0",
  trip_payouts_slab_km: "0",
  trip_payouts_extra_kms: "0",
  trip_payouts_days_used: "0.00",
  trip_payouts_hours_used: "0:00",
  trip_payouts_slab_hour: "0.00",
  trip_payouts_slab_rate: "0.00",
  trip_payouts_slab_os_rate: "0.00",
  trip_payouts_charge_amount: "0.00",
  trip_payouts_extra_hour: "0.00",
  trip_payouts_slab_extra_hour_rate: "0.00",
  trip_payouts_extra_hour_amount: "0.00",
  trip_payouts_slab_extra_km_rate: "0.00",
  trip_payouts_extra_km_amount: "0.00",
  trip_payouts_fuel_esculation_rate: "0.00",
  trip_payouts_fuel_esculation_amount: "0.00",
  trip_payouts_local_batta: "0.00",
  trip_payouts_no_of_os_batta: "0.00",
  trip_payouts_os_batta_per_day: "0.00",
  trip_payouts_os_batta_total_amount: "0.00",
  trip_payouts_no_of_os_night_batta: "0.00",
  trip_payouts_os_night_batta: "0.00",
  trip_payouts_os_night_batta_total_amount: "0.00",
  trip_payouts_permit: "0.00",
  trip_payouts_parking: "0.00",
  trip_payouts_toll_fasttag: "0.00",
  trip_payouts_others: "0.00",
  trip_payouts_escort_amount: "0.00",
  trip_payouts_advance: "0.00",
  trip_payouts_booking_advance: "0.00",
  trip_payouts_hire_amount: "0.00",
  trip_payouts_fuel_esculation_yes_no: "",
  trip_payouts_escort_yes_no: "",
  trip_payouts_advance_date: "",
  trip_payouts_duty: "",
  trip_payouts_vehicle_group_id: "0",
};

function debounce(fn, delay) {
  let timer;
  return (...args) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

export default function PayoutsForm({
  payouts,
  setPayouts,
  resetSignal,
  dutyForm,
  isVendorVehicle,
  tariff,
  tariffSlabs,
  tripPayoutsDaysUsed,
  tripPayoutsHoursUsed,
  tripPayoutsKmsUsed,
  hoursUsedDisplay,
  selectedDutyType,
  dutyEditIndex,
  customerId,
  clientId,
  setTripPayoutsDaysUsed,
  setTripPayoutsKmsUsed,
  tripPayoutsUsed,
  tripHoursUsed,
  tripKmsUsed,
  handleDutyFormChange,
  selectedVehicleGroupId,
  vehicleGroups,
  vendorName,
  vehicleNumber,
  vendorVehicleNo,
  setSelectedDutyType,
  setSelectedVehicleGroupId,
  payoutsUsageTimeRoundoff,
  ref,
}) {
  const [values, setValues] = useState(initialPayoutsState);
  const [prevPayouts, setPrevPayouts] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [manuallyEditedFields, setManuallyEditedFields] = useState(new Set());

  const debouncedSetEditingFalse = useRef(null);
  const tariffAppliedRef = useRef(false);

  const [payoutsTariff, setPayoutsTariff] = useState(null);
  const [payoutsTariffSlabs, setPayoutsTariffSlabs] = useState([]);

  const selectedVehicleGroupName =
    vehicleGroups?.find(
      (vg) => String(vg.categoryid) === String(selectedVehicleGroupId),
    )?.categoryname || "";

  const displayLabel = isVendorVehicle ? "Vendor:" : "Vehicle:";
  const displayText = isVendorVehicle
    ? `${vendorName}${vendorVehicleNo ? ` (${vendorVehicleNo})` : ""}`
    : vehicleNumber;

  // const [localDutyType, setLocalDutyType] = useState(selectedDutyType || "");
  // const [localVehicleGroupId, setLocalVehicleGroupId] = useState(
  //   selectedVehicleGroupId || ""
  // );

  const [localDutyType, setLocalDutyType] = useState(
    dutyForm?.payouts?.trip_payouts_duty || "",
  );
  const [localVehicleGroupId, setLocalVehicleGroupId] = useState(
    dutyForm?.payouts?.trip_payouts_vehicle_group_id || "",
  );

  useImperativeHandle(ref, () => ({
    getValues: () => values,

    setLocalDutyType: (dutyType) => {
      setLocalDutyType(dutyType);
    },

    setLocalVehicleGroupId: (vehicleGroupId) => {
      setLocalVehicleGroupId(vehicleGroupId);
    },

    setValues: (newValues) => {
      setValues(newValues);
    },
  }));

  // Load saved payouts only in edit mode.
  // New payouts are initialized by the tariff effect below.
  useEffect(() => {
    if (payouts && Object.keys(payouts).length > 0) {
      setValues((prev) => ({
        ...prev,
        ...payouts,
      }));

      setIsEditing(true);

      const timer = setTimeout(() => {
        setIsEditing(false);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [payouts]);

  // Reset manual editing on tariff change or resetSignal
  useEffect(() => {
    setManuallyEditedFields(new Set());
    tariffAppliedRef.current = false;
  }, [tariff, tariffSlabs, selectedDutyType, resetSignal]);

  const mergePreserveFields = useCallback((prev, newData) => {
    const merged = { ...prev, ...newData };
    const preferOldIfZero = (field) => {
      if (!newData?.[field] || newData[field] === "0.00") {
        merged[field] = prev[field];
      }
    };
    [
      "trip_payouts_local_batta",
      "trip_payouts_extra_km_amount",
      "trip_payouts_extra_hour_amount",
      "trip_payouts_fuel_esculation_rate",
      "trip_payouts_fuel_esculation_amount",
      "trip_payouts_no_of_os_batta",
      "trip_payouts_os_batta_per_day",
      "trip_payouts_os_batta_total_amount",
      "trip_payouts_no_of_os_night_batta",
      "trip_payouts_os_night_batta",
      "trip_payouts_os_night_batta_total_amount",
      "trip_payouts_permit",
      "trip_payouts_parking",
      "trip_payouts_toll_fasttag",
      "trip_payouts_others",
      "trip_payouts_escort_amount",
    ].forEach(preferOldIfZero);
    return merged;
  }, []);

  // Apply payout tariff values once when tariff/slab is ready.
  useEffect(() => {
    if (payouts && Object.keys(payouts).length > 0) return;

    if (!tariff || !tariffSlabs?.length) return;

    if (tariffAppliedRef.current) return;

    const slab = tariffSlabs[0];

    const nextValues = {
      ...initialPayoutsState,

      trip_payouts_days_used:
        tripPayoutsDaysUsed !== undefined
          ? tripPayoutsDaysUsed.toString()
          : "0.00",

      trip_payouts_hours_used:
        tripPayoutsHoursUsed !== undefined
          ? decimalHoursToHHMM(tripPayoutsHoursUsed)
          : "0:00",

      trip_payouts_kms_used:
        tripPayoutsKmsUsed !== undefined ? tripPayoutsKmsUsed.toString() : "0",

      trip_payouts_slab_rate: slab?.purchase_slab_rate
        ? parseFloat(slab.purchase_slab_rate).toFixed(2)
        : "0.00",

      trip_payouts_slab_km:
        slab?.kms !== undefined ? parseFloat(slab.kms).toFixed(2) : "0.00",

      trip_payouts_slab_hour:
        slab?.hours !== undefined ? parseFloat(slab.hours).toFixed(2) : "0.00",

      trip_payouts_slab_extra_km_rate: tariff?.purchase_extra_km_rate
        ? parseFloat(tariff.purchase_extra_km_rate).toFixed(2)
        : "0.00",

      trip_payouts_slab_extra_hour_rate: tariff?.purchase_extra_hour_rate
        ? parseFloat(tariff.purchase_extra_hour_rate).toFixed(2)
        : "0.00",

      trip_payouts_fuel_esculation_rate: tariff?.purchase_fuel_escalation_per_km
        ? parseFloat(tariff.purchase_fuel_escalation_per_km).toFixed(2)
        : "0.00",

      trip_payouts_escort_amount: tariff?.purchase_escort_amount
        ? parseFloat(tariff.purchase_escort_amount).toFixed(2)
        : "0.00",

      trip_payouts_charge_amount: slab?.purchase_slab_rate
        ? parseFloat(slab.purchase_slab_rate).toFixed(2)
        : "0.00",

      trip_payouts_hire_amount: slab?.purchase_slab_rate
        ? parseFloat(slab.purchase_slab_rate).toFixed(2)
        : "0.00",
    };

    setValues((prev) => ({
      ...nextValues,

      // Preserve manually entered values.
      trip_payouts_local_batta: prev.trip_payouts_local_batta || "0.00",

      trip_payouts_parking: prev.trip_payouts_parking || "0.00",

      trip_payouts_permit: prev.trip_payouts_permit || "0.00",

      trip_payouts_toll_fasttag: prev.trip_payouts_toll_fasttag || "0.00",

      trip_payouts_others: prev.trip_payouts_others || "0.00",
    }));

    tariffAppliedRef.current = true;
  }, [
    tariff,
    tariffSlabs,
    resetSignal,
    payouts,
    tripPayoutsDaysUsed,
    tripPayoutsHoursUsed,
    tripPayoutsKmsUsed,
  ]);

  // Auto-calc extras kms and hours but respect manual edits
  useEffect(() => {
    if (tariffAppliedRef.current && manuallyEditedFields.size > 0) return;
    const slab = tariffSlabs?.[0] || {};
    const kmSlab = parseFloat(slab.kms || values.trip_payouts_slab_km || 0);
    const hrSlab = parseFloat(slab.hours || values.trip_payouts_slab_hour || 0);
    const baseRate = parseFloat(
      slab.rate || values.trip_payouts_slab_rate || 0,
    );

    setValues((prev) => {
      const usedKms = parseFloat(prev.trip_payouts_kms_used || 0);

      const usedHours = parseFloat(
        hhmmToDecimalHours(prev.trip_payouts_hours_used || "0:00"),
      );

      const extraKms = Math.max(0, usedKms - kmSlab);
      const extraHours =
        payoutsUsageTimeRoundoff?.chargeable_minutes != null
          ? payoutsUsageTimeRoundoff.chargeable_minutes / 60
          : Math.max(0, usedHours - hrSlab);

      const extraKmRate =
        prev.trip_payouts_slab_extra_km_rate ||
        tariff?.purchase_extra_km_rate ||
        slab.extra_km_rate ||
        0;

      const extraHrRate =
        prev.trip_payouts_slab_extra_hour_rate ||
        tariff?.purchase_extra_hour_rate ||
        slab.extra_hour_rate ||
        0;

      const extraKmAmount = extraKms * parseFloat(extraKmRate);

      const extraHrAmount = extraHours * parseFloat(extraHrRate);

      const nextValues = {
        ...prev,
        trip_payouts_slab_km: kmSlab.toFixed(2),

        trip_payouts_slab_hour: hrSlab.toFixed(2),

        trip_payouts_slab_rate: baseRate.toFixed(2),

        trip_payouts_slab_extra_km_rate: parseFloat(extraKmRate).toFixed(2),

        trip_payouts_slab_extra_hour_rate: parseFloat(extraHrRate).toFixed(2),

        trip_payouts_extra_kms: extraKms.toFixed(2),

        trip_payouts_extra_hour: extraHours.toFixed(2),

        trip_payouts_extra_km_amount: extraKmAmount.toFixed(2),

        trip_payouts_extra_hour_amount: extraHrAmount.toFixed(2),
      };

      const changed = Object.keys(nextValues).some(
        (key) => nextValues[key] !== prev[key],
      );

      return changed ? nextValues : prev;
    });
  }, [
    tariff,
    tariffSlabs,
    selectedDutyType,
    tripPayoutsUsed,
    tripHoursUsed,
    tripKmsUsed,
    values.trip_payouts_kms_used,
    values.trip_payouts_hours_used,
    values.trip_payouts_slab_km,
    values.trip_payouts_slab_hour,
    values.trip_payouts_slab_rate,
    values.trip_payouts_slab_extra_km_rate,
    values.trip_payouts_slab_extra_hour_rate,
    manuallyEditedFields,
    payoutsUsageTimeRoundoff,
  ]);

  // -------Tariff Fetch--------------------

  useEffect(() => {
    console.log("🟣 [InitEffect] Checking payoutsDuty & payoutsVehicleGroupId");

    // If editing, always use the saved payout duty
    if (dutyEditIndex >= 0) {
      const dutyTypeToSet =
        dutyForm?.payouts?.trip_payouts_duty || dutyForm?.duty || "";
      console.log("🟢 Editing mode: setting localDutyType →", dutyTypeToSet);
      setLocalDutyType(dutyTypeToSet);
      setSelectedDutyType && setSelectedDutyType(dutyTypeToSet);

      const vehicleGroupToSet =
        dutyForm?.payouts?.trip_payouts_vehicle_group_id ||
        dutyForm?.assigned_vehicle_group_id ||
        "";
      console.log(
        "🟢 Editing mode: setting localVehicleGroupId →",
        vehicleGroupToSet,
      );
      setLocalVehicleGroupId(vehicleGroupToSet);
      setSelectedVehicleGroupId && setSelectedVehicleGroupId(vehicleGroupToSet);
    } else {
      // Initialize only if not editing
      const dutyTypeToSet =
        dutyForm?.payouts?.trip_payouts_duty || dutyForm?.duty || "";
      const vehicleGroupToSet =
        dutyForm?.payouts?.trip_payouts_vehicle_group_id ||
        dutyForm?.assigned_vehicle_group_id ||
        "";

      setLocalDutyType(dutyTypeToSet);
      setSelectedDutyType && setSelectedDutyType(dutyTypeToSet);

      setLocalVehicleGroupId(vehicleGroupToSet);
      setSelectedVehicleGroupId && setSelectedVehicleGroupId(vehicleGroupToSet);
    }
  }, [dutyForm, dutyEditIndex]);

  useEffect(() => {
    if (selectedDutyType) {
      setLocalDutyType(selectedDutyType);
    }
  }, [selectedDutyType]);

  useEffect(() => {
    if (selectedVehicleGroupId !== localVehicleGroupId) {
      setLocalVehicleGroupId(selectedVehicleGroupId || "");
    }
  }, [selectedVehicleGroupId, isEditing]);

  // 🧠 Apply correct payout strategy based on duty type
  useEffect(() => {
    if (isEditing) return;
    const strategy =
      dutyTypeStrategies[localDutyType] || dutyTypeStrategies.default;
    const slab = tariffSlabs?.[0];

    const stateUpdate = strategy({
      values,
      tariff,
      slab,
      tripPayouts: tripPayoutsUsed,
      tripHours: tripHoursUsed,
      tripKms: tripKmsUsed,
    });

    if (stateUpdate) {
      setValues((prev) => ({
        ...prev,
        ...stateUpdate,
      }));
    }
  }, [
    tariff,
    tariffSlabs,
    tripPayoutsUsed,
    tripHoursUsed,
    tripKmsUsed,
    localDutyType,
    isEditing,
  ]);

  // ------------------------------------------------

  // Sync parent usage props only if not editing manually
  useEffect(() => {
    if (isEditing) return;
    if (
      tripPayoutsDaysUsed !== undefined &&
      tripPayoutsHoursUsed !== undefined &&
      tripPayoutsKmsUsed !== undefined
    ) {
      setValues((prev) => ({
        ...prev,
        trip_payouts_days_used: tripPayoutsDaysUsed.toString(),
        trip_payouts_hours_used: decimalHoursToHHMM(tripPayoutsHoursUsed),
        trip_payouts_kms_used: tripPayoutsKmsUsed.toString(),
      }));
    }
  }, [
    tripPayoutsDaysUsed,
    tripPayoutsHoursUsed,
    tripPayoutsKmsUsed,
    isEditing,
  ]);

  // Update parent total payouts on usage or tariff changes
  useEffect(() => {
    if (!tariff) {
      setPayouts(0);
      return;
    }
    let total = 0;
    if (tripPayoutsDaysUsed && tariff.dayRate)
      total += tripPayoutsDaysUsed * tariff.dayRate;
    if (tripPayoutsHoursUsed && tariff.hourRate)
      total += tripPayoutsHoursUsed * tariff.hourRate;
    if (tripPayoutsKmsUsed && tariff.kmRate)
      total += tripPayoutsKmsUsed * tariff.kmRate;
    setPayouts(total);
  }, [
    tariff,
    tripPayoutsDaysUsed,
    tripPayoutsHoursUsed,
    tripPayoutsKmsUsed,
    setPayouts,
  ]);

  const handleChange = (field, value) => {
    setIsEditing(true);
    setManuallyEditedFields((prev) => new Set(prev).add(field));
    const updated = { ...values, [field]: value };
    setValues(updated);
    handleDutyFormChange("payouts", updated);

    if (window._payoutsEditTimer) clearTimeout(window._payoutsEditTimer);
    window._payoutsEditTimer = setTimeout(() => setIsEditing(false), 800);
  };

  const handleVehicleGroupChange = (newGroupId) => {
    setIsEditing(true);
    setLocalVehicleGroupId(newGroupId);
    handleDutyFormChange("payouts", {
      trip_payouts_vehicle_group_id: newGroupId,
    });
    if (setSelectedVehicleGroupId) setSelectedVehicleGroupId(newGroupId);
    // Optionally reset isEditing after short delay elsewhere
  };

  const handleBlur = () => {
    if (window._payoutsEditTimer) clearTimeout(window._payoutsEditTimer);
    window._payoutsEditTimer = setTimeout(() => setIsEditing(false), 300);
  };

  const renderField = (fieldName, placeholder) => {
    const isKmQuantityField =
      fieldName.toLowerCase().includes("kms_used") ||
      fieldName.toLowerCase().includes("extra_kms") ||
      fieldName.toLowerCase().endsWith("_km");
    const isHoursField = fieldName === "trip_payouts_hours_used";
    const isExtraHourField = fieldName === "trip_payouts_extra_hour";

    const getDisplayValue = () => {
      const val = values[fieldName];
      if (val === undefined || val === null || val === "") {
        if (isExtraHourField || isHoursField) return "0:00";
        if (isKmQuantityField) return "0";
        return "0.00";
      }
      if (isExtraHourField) return decimalHoursToHHMM(Number(val));
      if (isHoursField) return val.toString();

      if (isKmQuantityField) {
        const num = parseFloat(val);
        return !isNaN(num) ? Math.floor(num).toString() : val.toString();
      }

      const num = parseFloat(val);
      return !isNaN(num) ? num.toFixed(2) : val.toString();
    };

    const handleChangeInternal = (e) => {
      let val = e.target.value;
      if (!isHoursField && !isExtraHourField) {
        val = val.replace(/[^0-9.]/g, "");
        const parts = val.split(".");
        if (parts.length > 2) val = parts[0] + "." + parts.slice(1).join("");
      } else {
        val = val.replace(/[^0-9:]/g, "");
        const countColons = (val.match(/:/g) || []).length;
        if (countColons > 1) val = val.substring(0, val.lastIndexOf(":"));
      }
      handleChange(fieldName, val);
    };

    const handleBlurInternal = () => {
      if (!isHoursField && !isExtraHourField) {
        const val = parseFloat(values[fieldName]);
        if (!isNaN(val)) {
          handleChange(
            fieldName,
            isKmQuantityField ? Math.floor(val).toString() : val.toFixed(2),
          );
        }
      }
    };

    return (
      <TextField
        size="small"
        variant="outlined"
        placeholder={placeholder}
        value={getDisplayValue()}
        onChange={handleChangeInternal}
        onBlur={() => {
          handleBlurInternal();
          handleBlur();
        }}
        sx={commonInputStyle}
        inputProps={{ style: { textAlign: "right" } }}
        type="text"
      />
    );
  };

  /*
    Keep your existing payout duty type-specific calculations as useEffects here,
    unchanged to preserve your business logic.
  */

  useEffect(() => {
    console.log("Local Triggered..");
    if (selectedDutyType === "Local") {
      if (isEditing) return;

      const slabRate = parseFloat(values.trip_payouts_slab_rate || 0);

      // Convert hours used to decimal
      const hoursUsedDecimal = hhmmToDecimalHours(
        values.trip_payouts_hours_used || "0:00",
      );
      const kmsUsed = parseFloat(values.trip_payouts_kms_used || 0);

      // Slab limits
      const slabHour = parseFloat(values.trip_payouts_slab_hour || 0);
      const slabKm = parseFloat(values.trip_payouts_slab_km || 0);

      // Extra calculations
      console.log("hoursUsedDecimal", hoursUsedDecimal);
      console.log("slabHour", slabHour);

      const extraHour =
        payoutsUsageTimeRoundoff?.chargeable_minutes != null
          ? payoutsUsageTimeRoundoff.chargeable_minutes / 60
          : Math.max(0, hoursUsedDecimal - slabHour);
      const extraKms = Math.max(0, kmsUsed - slabKm);

      const extraHourAmount =
        extraHour * parseFloat(values.trip_payouts_slab_extra_hour_rate || 0);
      const extraKmAmount =
        extraKms * parseFloat(values.trip_payouts_slab_extra_km_rate || 0);

      // Fuel escalation
      const fuelEscRate =
        values.trip_payouts_fuel_esculation_yes_no === "Yes"
          ? parseFloat(tariff?.purchase_fuel_escalation_per_km || 0)
          : 0;
      const fuelEscAmount = fuelEscRate * kmsUsed;

      // Escort
      const escortAmount =
        values.trip_payouts_escort_yes_no === "Yes"
          ? parseFloat(tariff?.purchase_escort_amount || 0)
          : 0;

      // Total hire amount
      const hireAmount =
        slabRate +
        extraKmAmount +
        extraHourAmount +
        fuelEscAmount +
        (parseFloat(values.trip_payouts_local_batta) || 0) +
        (parseFloat(values.trip_payouts_permit) || 0) +
        (parseFloat(values.trip_payouts_parking) || 0) +
        (parseFloat(values.trip_payouts_toll_fasttag) || 0) +
        (parseFloat(values.trip_payouts_others) || 0) +
        escortAmount -
        (parseFloat(values.trip_payouts_advance) || 0);

      setValues((prev) => ({
        ...prev,
        trip_payouts_charge_amount: slabRate.toFixed(2),
        trip_payouts_extra_kms: Math.round(extraKms).toString(), // no decimals
        trip_payouts_extra_hour: extraHour.toFixed(2),
        trip_payouts_extra_km_amount: extraKmAmount.toFixed(2),
        trip_payouts_extra_hour_amount: extraHourAmount.toFixed(2),
        trip_payouts_fuel_esculation_rate: fuelEscRate.toFixed(2),
        trip_payouts_fuel_esculation_amount: fuelEscAmount.toFixed(2),
        trip_payouts_escort_amount: escortAmount.toFixed(2),
        trip_payouts_hire_amount: hireAmount.toFixed(2),
      }));
    }
  }, [
    selectedDutyType,
    // values,
    values.trip_payouts_slab_rate,
    values.trip_payouts_slab_extra_km_rate,
    values.trip_payouts_slab_extra_hour_rate,
    values.trip_payouts_hours_used,
    values.trip_payouts_kms_used,
    values.trip_payouts_slab_hour,
    values.trip_payouts_slab_km,
    values.trip_payouts_permit,
    values.trip_payouts_parking,
    values.trip_payouts_toll_fasttag,
    values.trip_payouts_others,
    values.trip_payouts_escort_yes_no,
    values.trip_payouts_fuel_esculation_yes_no,
    values.trip_payouts_advance,
    tariff,
    tariffSlabs,
    values.trip_payouts_duty,
    values.trip_payouts_vehicle_group_id,
    localDutyType,
    localVehicleGroupId,
    isEditing,
    payoutsUsageTimeRoundoff,
  ]);

  // useEffect for OUTSTATION Duty Type
  useEffect(() => {
    if (selectedDutyType !== "Outstation") return;
    if (isEditing) return;
    const safeParseNonZero = (val, fallback) => {
      const p = parseFloat(val);
      // returns parsed value if falsy or less than 100, else fallback
      return !p || p < 100 ? p : fallback;
    };

    const slabOsRate = safeParseNonZero(
      values.trip_payouts_slab_os_rate &&
        values.trip_payouts_slab_os_rate !== "0" &&
        values.trip_payouts_slab_os_rate !== "0.00"
        ? values.trip_payouts_slab_os_rate
        : tariff?.purchase_os_km_rate || 0,
      0,
    );

    console.log(
      "Slab OS Rate raw value:",
      values.trip_payouts_slab_os_rate,
      tariff?.purchase_os_km_rate,
    );

    const minKmPerDay = parseFloat(tariff?.purchase_os_min_kms || 0);
    const usedDays = parseFloat(values.trip_payouts_days_used || 1);

    const slabKms = minKmPerDay * usedDays;

    const kmsUsed = parseFloat(values.trip_payouts_kms_used || 0);

    const chargeKms = Math.max(kmsUsed, slabKms);
    const baseChargeAmount = slabOsRate * chargeKms;

    const safeParse = (val, fallback = 0) => {
      const parsed = parseFloat(val);
      return isNaN(parsed) || parsed === 0 ? fallback : parsed;
    };

    const noOfOsBatta = parseFloat(values.trip_payouts_days_used || 1);

    const osBattaPerDay = safeParse(
      values.trip_payouts_os_batta_per_day,
      safeParse(tariff?.purchase_driver_batta, 0),
    );

    const noOfOsNightBatta = safeParse(
      values.trip_payouts_no_of_os_night_batta,
      0,
    );
    const osNightBatta = safeParse(
      values.trip_payouts_os_night_batta,
      safeParse(tariff?.purchase_night_batta, 0),
    );

    const osBattaTotalAmount = noOfOsBatta * osBattaPerDay;
    const osNightBattaTotalAmount = noOfOsNightBatta * osNightBatta;

    // Fuel escalation calculation added
    const fuelEscRate =
      values.trip_payouts_fuel_esculation_yes_no === "Yes"
        ? parseFloat(tariff?.purchase_fuel_escalation_per_km || 0)
        : 0;
    const fuelEscAmount =
      fuelEscRate * (parseFloat(values.trip_payouts_kms_used) || 0);

    // Escort calculation added
    const escortAmount =
      values.trip_payouts_escort_yes_no === "Yes"
        ? parseFloat(tariff?.purchase_escort_amount || 0)
        : 0;

    const totalHireAmount =
      baseChargeAmount +
      osBattaTotalAmount +
      osNightBattaTotalAmount +
      fuelEscAmount +
      (parseFloat(values.trip_payouts_local_batta) || 0) +
      (parseFloat(values.trip_payouts_permit) || 0) +
      (parseFloat(values.trip_payouts_parking) || 0) +
      (parseFloat(values.trip_payouts_toll_fasttag) || 0) +
      (parseFloat(values.trip_payouts_others) || 0) +
      escortAmount -
      ((parseFloat(values.trip_payouts_advance) || 0) +
        (parseFloat(values.trip_payouts_booking_advance) || 0));

    setValues((prev) => ({
      ...prev,
      trip_payouts_slab_os_rate: slabOsRate.toString(),
      trip_payouts_slab_km: slabKms.toString(),
      trip_payouts_charge_amount: baseChargeAmount.toFixed(2),
      trip_payouts_no_of_os_batta: noOfOsBatta.toString(),
      trip_payouts_os_batta_per_day: osBattaPerDay.toFixed(2),
      trip_payouts_os_batta_total_amount: osBattaTotalAmount.toFixed(2),
      trip_payouts_no_of_os_night_batta: noOfOsNightBatta.toString(),
      trip_payouts_os_night_batta: osNightBatta.toFixed(2),
      trip_payouts_os_night_batta_total_amount:
        osNightBattaTotalAmount.toFixed(2),
      trip_payouts_fuel_esculation_rate: fuelEscRate.toFixed(2),
      trip_payouts_fuel_esculation_amount: fuelEscAmount.toFixed(2),
      trip_payouts_escort_amount: escortAmount.toFixed(2),
      trip_payouts_hire_amount: totalHireAmount.toFixed(2),
    }));
  }, [
    selectedDutyType,
    values.trip_payouts_slab_os_rate,
    values.trip_payouts_slab_km,
    values.trip_payouts_no_of_os_batta,
    values.trip_payouts_os_batta_per_day,
    values.trip_payouts_kms_used,
    values.trip_payouts_no_of_os_night_batta,
    values.trip_payouts_permit,
    values.trip_payouts_parking,
    values.trip_payouts_toll_fasttag,
    values.trip_payouts_others,
    values.trip_payouts_escort_yes_no,
    values.trip_payouts_fuel_esculation_yes_no,
    values.trip_payouts_advance,
    values.trip_payouts_duty,
    values.trip_payouts_vehicle_group_id,
    localDutyType,
    localVehicleGroupId,
    tariff,
    isEditing,
  ]);

  // UseEffect for AirportTransfer Duty Type
  useEffect(() => {
    if (selectedDutyType !== "AirportTransfer") return;

    if (isEditing) return;
    const hoursUsedDecimal = hhmmToDecimalHours(
      values.trip_payouts_hours_used || "0:00",
    );
    const kmsUsed = parseFloat(values.trip_payouts_kms_used || 0);

    // Slab limits
    const slabHour = parseFloat(values.trip_payouts_slab_hour || 0);
    const slabKm = parseFloat(values.trip_payouts_slab_km || 0);

    // Slab rate editable or fallback to tariff
    const slabRate =
      parseFloat(values.trip_payouts_slab_rate) ||
      parseFloat(tariff?.purchase_airport_transfer_rate || 0);

    // Extra calculations (strict from usage - slab)
    const extraHour =
      payoutsUsageTimeRoundoff?.chargeable_minutes != null
        ? payoutsUsageTimeRoundoff.chargeable_minutes / 60
        : Math.max(0, hoursUsedDecimal - slabHour);
    const extraKms = Math.max(0, kmsUsed - slabKm);

    const extraHourAmount =
      extraHour * parseFloat(values.trip_payouts_slab_extra_hour_rate || 0);
    const extraKmAmount =
      extraKms * parseFloat(values.trip_payouts_slab_extra_km_rate || 0);

    // Fuel escalation calculation
    const fuelEscRate =
      values.trip_payouts_fuel_esculation_yes_no === "Yes"
        ? parseFloat(tariff?.purchase_fuel_escalation_per_km || 0)
        : 0;
    const fuelEscAmount = fuelEscRate * kmsUsed;

    // Escort calculation
    const escortAmount =
      values.trip_payouts_escort_yes_no === "Yes"
        ? parseFloat(tariff?.purchase_escort_amount || 0)
        : 0;

    // Charge amount equals slab rate + extra amounts
    const chargeAmount =
      slabRate + extraHourAmount + extraKmAmount + fuelEscAmount + escortAmount;

    // Total hire amount including extras and deductions
    const hireAmount =
      chargeAmount +
      (parseFloat(values.trip_payouts_permit) || 0) +
      (parseFloat(values.trip_payouts_parking) || 0) +
      (parseFloat(values.trip_payouts_toll_fasttag) || 0) +
      (parseFloat(values.trip_payouts_others) || 0) -
      (parseFloat(values.trip_payouts_advance) || 0);

    setValues((prev) => ({
      ...prev,
      trip_payouts_slab_hour:
        values.trip_payouts_slab_hour || slabHour.toString(),
      trip_payouts_slab_km: values.trip_payouts_slab_km || slabKm.toString(),
      trip_payouts_extra_hour: extraHour.toFixed(2),
      trip_payouts_extra_kms: extraKms.toFixed(2),
      trip_payouts_extra_hour_amount: extraHourAmount.toFixed(2),
      trip_payouts_extra_km_amount: extraKmAmount.toFixed(2),
      trip_payouts_fuel_esculation_rate: fuelEscRate.toFixed(2),
      trip_payouts_fuel_esculation_amount: fuelEscAmount.toFixed(2),
      trip_payouts_escort_amount: escortAmount.toFixed(2),
      trip_payouts_slab_rate: slabRate.toFixed(2),
      trip_payouts_charge_amount: slabRate.toFixed(2),
      trip_payouts_hire_amount: hireAmount.toFixed(2),
    }));
  }, [
    selectedDutyType,
    values,
    values.trip_payouts_hours_used,
    values.trip_payouts_kms_used,
    values.trip_payouts_slab_hour,
    values.trip_payouts_slab_km,
    values.trip_payouts_slab_extra_hour_rate,
    values.trip_payouts_slab_extra_km_rate,
    values.trip_payouts_fuel_esculation_yes_no,
    values.trip_payouts_escort_yes_no,
    values.trip_payouts_slab_rate,
    values.trip_payouts_permit,
    values.trip_payouts_parking,
    values.trip_payouts_toll_fasttag,
    values.trip_payouts_others,
    values.trip_payouts_advance,
    tariff,
    localDutyType,
    localVehicleGroupId,
    isEditing,
    payoutsUsageTimeRoundoff,
  ]);

  //UseEffect for EmployeeTransfer Duty Type
  useEffect(() => {
    if (selectedDutyType !== "EmployeeTransfer") return;
    if (isEditing) return;
    // Convert hours used to decimal
    const hoursUsedDecimal = hhmmToDecimalHours(
      values.trip_payouts_hours_used || "0:00",
    );
    const kmsUsed = parseFloat(values.trip_payouts_kms_used || 0);

    // Slab limits
    const slabHour = parseFloat(values.trip_payouts_slab_hour || 0);
    const slabKm = parseFloat(values.trip_payouts_slab_km || 0);

    // Extra calculations
    const extraHour =
      payoutsUsageTimeRoundoff?.chargeable_minutes != null
        ? payoutsUsageTimeRoundoff.chargeable_minutes / 60
        : Math.max(0, hoursUsedDecimal - slabHour);
    const extraKms = Math.max(0, kmsUsed - slabKm);

    const extraHourAmount =
      extraHour * parseFloat(values.trip_payouts_slab_extra_hour_rate || 0);
    const extraKmAmount =
      extraKms * parseFloat(values.trip_payouts_slab_extra_km_rate || 0);

    // Fuel escalation calculation
    const fuelEscRate =
      values.trip_payouts_fuel_esculation_yes_no === "Yes"
        ? parseFloat(tariff?.purchase_fuel_escalation_per_km || 0)
        : 0;
    const fuelEscAmount = fuelEscRate * kmsUsed;

    // Escort calculation
    const escortAmount =
      values.trip_payouts_escort_yes_no === "Yes"
        ? parseFloat(tariff?.purchase_escort_amount || 0)
        : 0;

    // Slab rate editable or fallback to employee transfer tariff rate
    const slabRate =
      parseFloat(values.trip_payouts_slab_rate) ||
      parseFloat(tariff?.purchase_employee_transfer_rate || 0);

    // Charge amount = slab rate + extras
    const chargeAmount =
      slabRate + extraHourAmount + extraKmAmount + fuelEscAmount + escortAmount;

    // Total hire amount including extras and deductions
    const hireAmount =
      chargeAmount +
      (parseFloat(values.trip_payouts_permit) || 0) +
      (parseFloat(values.trip_payouts_parking) || 0) +
      (parseFloat(values.trip_payouts_toll_fasttag) || 0) +
      (parseFloat(values.trip_payouts_others) || 0) -
      (parseFloat(values.trip_payouts_advance) || 0);

    setValues((prev) => ({
      ...prev,
      trip_payouts_slab_hour:
        values.trip_payouts_slab_hour || slabHour.toString(),
      trip_payouts_slab_km: values.trip_payouts_slab_km || slabKm.toString(),
      trip_payouts_extra_hour: extraHour.toFixed(2),
      trip_payouts_extra_kms: extraKms.toFixed(2),
      trip_payouts_extra_hour_amount: extraHourAmount.toFixed(2),
      trip_payouts_extra_km_amount: extraKmAmount.toFixed(2),
      trip_payouts_fuel_esculation_rate: fuelEscRate.toFixed(2),
      trip_payouts_fuel_esculation_amount: fuelEscAmount.toFixed(2),
      trip_payouts_escort_amount: escortAmount.toFixed(2),
      trip_payouts_slab_rate: slabRate.toFixed(2),
      trip_payouts_charge_amount: slabRate.toFixed(2),
      trip_payouts_hire_amount: hireAmount.toFixed(2),
    }));
  }, [
    selectedDutyType,
    values,
    values.trip_payouts_hours_used,
    values.trip_payouts_kms_used,
    values.trip_payouts_slab_hour,
    values.trip_payouts_slab_km,
    values.trip_payouts_slab_extra_hour_rate,
    values.trip_payouts_slab_extra_km_rate,
    values.trip_payouts_fuel_esculation_yes_no,
    values.trip_payouts_escort_yes_no,
    values.trip_payouts_slab_rate,
    values.trip_payouts_permit,
    values.trip_payouts_parking,
    values.trip_payouts_toll_fasttag,
    values.trip_payouts_others,
    values.trip_payouts_advance,
    tariff,
    localDutyType,
    localVehicleGroupId,
    isEditing,
    payoutsUsageTimeRoundoff,
  ]);

  useEffect(() => {
    if (selectedDutyType !== "Contract") return;
    if (isEditing) return;
    const hoursUsed = values.trip_payouts_hours_used || "0:00";
    const kmsUsed = parseFloat(values.trip_payouts_kms_used || 0);

    setValues((prev) => ({
      ...prev,
      trip_payouts_hours_used: hoursUsed,
      trip_payouts_kms_used: kmsUsed.toString(),

      // Clear or hide other calculation fields
      trip_payouts_slab_hour: "",
      trip_payouts_slab_km: "",
      trip_payouts_extra_hour: "",
      trip_payouts_extra_kms: "",
      trip_payouts_extra_hour_amount: "",
      trip_payouts_extra_km_amount: "",
      trip_payouts_fuel_esculation_rate: "",
      trip_payouts_fuel_esculation_amount: "",
      trip_payouts_escort_amount: "",
      trip_payouts_slab_rate: "",
      trip_payouts_charge_amount: "",
      trip_payouts_hire_amount: "",
    }));
  }, [
    selectedDutyType,
    values,
    values.trip_payouts_hours_used,
    values.trip_payouts_kms_used,
    localDutyType,
    localVehicleGroupId,
    isEditing,
  ]);

  // On values or relevant dependencies change, run calculation
  useEffect(() => {
    if (selectedDutyType !== "OSDayRate") return;
    if (isEditing) return;
    const daysUsed = parseFloat(values.trip_payouts_days_used) || 1;
    const kmsUsed = parseFloat(values.trip_payouts_kms_used) || 0;

    const safeParseNonZero = (val, fallback) => {
      const p = parseFloat(val);
      return !p || p < 100 ? fallback : p;
    };

    const slabOsRate = safeParseNonZero(
      values.trip_payouts_slab_os_rate,
      parseFloat(tariff?.purchase_os_day_rate) || 0,
    );

    console.log(
      "OS day rate ",
      values.trip_payouts_slab_os_rate,
      tariff?.purchase_os_day_rate,
    );

    const minKmsPerDay = parseFloat(tariff?.purchase_os_min_kms ?? 0);
    const slabKms = minKmsPerDay * daysUsed;

    const slabExtraKmRate = parseFloat(tariff?.purchase_os_km_rate ?? 0);
    const baseChargeAmount = slabOsRate * daysUsed;

    const extraKms = Math.max(0, kmsUsed - slabKms);

    const extraKmAmount = extraKms * slabExtraKmRate;

    const noOfOsBatta = parseFloat(values.trip_payouts_days_used) || 1;
    const osBattaPerDay = safeParseNonZero(
      values.trip_payouts_os_batta_per_day,
      safeParseNonZero(tariff?.purchase_driver_batta, 0),
    );

    const noOfOsNightBatta = safeParseNonZero(
      values.trip_payouts_no_of_os_night_batta,
      0,
    );
    const osNightBatta = safeParseNonZero(
      values.trip_payouts_os_night_batta,
      safeParseNonZero(tariff?.purchase_night_batta, 0),
    );

    const osBattaTotalAmount = noOfOsBatta * osBattaPerDay;
    const osNightBattaTotalAmount = noOfOsNightBatta * osNightBatta;

    const fuelEscRate =
      values.trip_payouts_fuel_esculation_yes_no === "Yes"
        ? parseFloat(tariff?.purchase_fuel_escalation_per_km || 0)
        : 0;
    const fuelEscAmount = fuelEscRate * kmsUsed;

    const escortAmount =
      values.trip_payouts_escort_yes_no === "Yes"
        ? parseFloat(tariff?.purchase_escort_amount || 0)
        : 0;

    const totalHireAmount =
      baseChargeAmount +
      extraKmAmount +
      osBattaTotalAmount +
      osNightBattaTotalAmount +
      fuelEscAmount +
      (parseFloat(values.trip_payouts_local_batta) || 0) +
      (parseFloat(values.trip_payouts_permit) || 0) +
      (parseFloat(values.trip_payouts_parking) || 0) +
      (parseFloat(values.trip_payouts_toll_fasttag) || 0) +
      (parseFloat(values.trip_payouts_others) || 0) +
      escortAmount -
      ((parseFloat(values.trip_payouts_advance) || 0) +
        (parseFloat(values.trip_payouts_booking_advance) || 0));

    setValues((prev) => ({
      ...prev,
      trip_payouts_slab_os_rate: slabOsRate.toFixed(2),
      trip_payouts_slab_extra_km_rate: slabExtraKmRate.toString(),
      trip_payouts_slab_km: slabKms.toString(),
      trip_payouts_extra_kms: Math.floor(extraKms).toString(),
      trip_payouts_extra_km_amount: extraKmAmount.toFixed(2),
      trip_payouts_charge_amount: baseChargeAmount.toFixed(2),
      trip_payouts_no_of_os_batta: noOfOsBatta.toString(),
      trip_payouts_os_batta_per_day: osBattaPerDay.toFixed(2),
      trip_payouts_os_batta_total_amount: osBattaTotalAmount.toFixed(2),
      trip_payouts_no_of_os_night_batta: noOfOsNightBatta.toString(),
      trip_payouts_os_night_batta: osNightBatta.toFixed(2),
      trip_payouts_os_night_batta_total_amount:
        osNightBattaTotalAmount.toFixed(2),
      trip_payouts_fuel_esculation_rate: fuelEscRate.toFixed(2),
      trip_payouts_fuel_esculation_amount: fuelEscAmount.toFixed(2),
      trip_payouts_escort_amount: escortAmount.toFixed(2),
      trip_payouts_hire_amount: totalHireAmount.toFixed(2),
    }));
  }, [
    selectedDutyType,
    tariff,
    values,
    values.trip_payouts_slab_os_rate,
    values.trip_payouts_days_used,
    values.trip_payouts_kms_used,
    values.trip_payouts_no_of_os_batta,
    values.trip_payouts_os_batta_per_day,
    values.trip_payouts_no_of_os_night_batta,
    values.trip_payouts_os_night_batta,
    localDutyType,
    localVehicleGroupId,
    isEditing,
  ]);

  const dutyTypeStrategies = {
    // Modernized Local calculation function (2025 best practices)

    Local: ({ values, tariff, slab }) => {
      const hoursUsed = hhmmToDecimalHours(
        values?.trip_payouts_hours_used ?? "0:00",
      );
      const kmsUsed = parseFloat(values?.trip_payouts_kms_used ?? "0");

      const slabHour = parseFloat(slab?.hours ?? 0);
      const slabKm = parseFloat(slab?.kms ?? 0);
      const slabRate = parseFloat(slab?.purchase_slab_rate ?? 0);

      // Prefer slab-specific first, fallback to tariff, then 0
      const slabExtraHourRate = parseFloat(
        slab?.purchase_slab_extra_hour_rate ??
          tariff?.purchase_extra_hour_rate ??
          0,
      );
      const slabExtraKmRate = parseFloat(
        slab?.purchase_slab_extra_km_rate ??
          tariff?.purchase_extra_km_rate ??
          0,
      );

      const extraHour =
        payoutsUsageTimeRoundoff?.chargeable_minutes != null
          ? payoutsUsageTimeRoundoff.chargeable_minutes / 60
          : Math.max(0, hoursUsed - slabHour);
      const extraKms = Math.max(0, kmsUsed - slabKm);

      const extraHourAmount =
        extraHour *
        parseFloat(
          values?.trip_payouts_slab_extra_hour_rate ?? slabExtraHourRate,
        );
      const extraKmAmount =
        extraKms *
        parseFloat(values?.trip_payouts_slab_extra_km_rate ?? slabExtraKmRate);

      const totalHireAmount =
        slabRate +
        extraHourAmount +
        extraKmAmount +
        (parseFloat(values?.trip_payouts_fuel_esculation_amount) || 0) +
        (parseFloat(values?.trip_payouts_local_batta) || 0) +
        (parseFloat(values?.trip_payouts_permit) || 0) +
        (parseFloat(values?.trip_payouts_parking) || 0) +
        (parseFloat(values?.trip_payouts_toll_fasttag) || 0) +
        (parseFloat(values?.trip_payouts_others) || 0) +
        (parseFloat(values?.trip_payouts_escort_amount) || 0) -
        ((parseFloat(values?.trip_payouts_advance) || 0) +
          (parseFloat(values?.trip_payouts_booking_advance) || 0));

      return {
        trip_payouts_slab_hour: slabHour.toString(),
        trip_payouts_slab_km: slabKm.toString(),
        trip_payouts_slab_rate: slabRate.toFixed(2),
        trip_payouts_slab_extra_hour_rate: slabExtraHourRate.toString(),
        trip_payouts_slab_extra_km_rate: slabExtraKmRate.toString(),
        trip_payouts_charge_amount: slabRate.toFixed(2),
        trip_payouts_extra_hour: extraHour.toFixed(2),
        trip_payouts_extra_hours_used: extraHour.toFixed(2), // new field
        trip_payouts_extra_kms:
          extraKms > 0 && extraKms < 1 ? "1" : Math.floor(extraKms).toString(),

        trip_payouts_extra_kms_used: extraKms.toFixed(2), // new field
        trip_payouts_extra_hour_amount: extraHourAmount.toFixed(2),
        trip_payouts_extra_km_amount: extraKmAmount.toFixed(2),
        trip_payouts_hire_amount: totalHireAmount.toFixed(2),
      };
    },

    Outstation: ({ values, tariff }) => {
      if (!tariff) {
        // Return default values if tariff is null or undefined
        return {
          trip_payouts_days_used: "0",
          trip_payouts_kms_used: "0",
          trip_payouts_slab_km: "0",
          trip_payouts_charge_amount: "0",
          trip_payouts_hire_amount: "0",
          trip_payouts_slab_os_rate: "0",
          trip_payouts_no_of_os_batta: "0",
          trip_payouts_os_batta_per_day: "0",
          trip_payouts_os_batta_total_amount: "0",
          trip_payouts_no_of_os_night_batta: "0",
          trip_payouts_os_night_batta: "0",
          trip_payouts_os_night_batta_total_amount: "0",
          trip_payouts_slab_hour: "0",
          trip_payouts_slab_rate: "0",
          trip_payouts_extra_hour: "0",
          trip_payouts_extra_kms: "0",
          trip_payouts_slab_extra_hour_rate: "0",
          trip_payouts_slab_extra_km_rate: "0",
        };
      }

      const daysUsed = parseFloat(values.trip_payouts_days_used) || 1;
      const kmsUsed = parseFloat(values.trip_payouts_kms_used) || 0;
      const slabKmRate = parseFloat(tariff.purchase_os_km_rate) || 0;

      const minKmsPerDay = parseFloat(tariff.purchase_os_min_kms) || 0;

      const slabKms = minKmsPerDay * daysUsed;

      const baseChargeAmount =
        kmsUsed > slabKms ? kmsUsed * slabKmRate : slabKms * slabKmRate;

      const osDayBattaPerDay = parseFloat(tariff.purchase_driver_batta) || 0;
      const osNightBattaRate = parseFloat(tariff.purchase_night_batta) || 0;

      const noOfOsNightBatta =
        parseFloat(values.trip_payouts_no_of_os_night_batta) || 0;
      const osNightBattaTotal = noOfOsNightBatta * osNightBattaRate;
      const osDayBattaTotal = daysUsed * osDayBattaPerDay;

      const totalHireAmount =
        baseChargeAmount +
        osDayBattaTotal +
        osNightBattaTotal +
        (parseFloat(values.trip_payouts_fuel_esculation_amount) || 0) +
        (parseFloat(values.trip_payouts_local_batta) || 0) +
        (parseFloat(values.trip_payouts_permit) || 0) +
        (parseFloat(values.trip_payouts_parking) || 0) +
        (parseFloat(values.trip_payouts_toll_fasttag) || 0) +
        (parseFloat(values.trip_payouts_others) || 0) +
        (parseFloat(values.trip_payouts_escort_amount) || 0) -
        ((parseFloat(values.trip_payouts_advance) || 0) +
          (parseFloat(values.trip_payouts_booking_advance) || 0));

      console.log("Outstation Calculation:", {
        daysUsed,
        kmsUsed,
        minKmsPerDay,
        slabKms,
        slabKmRate,
        baseChargeAmount,
      });

      return {
        trip_payouts_days_used: daysUsed.toString(),
        trip_payouts_kms_used: kmsUsed.toString(),
        trip_payouts_slab_km: slabKms.toString(),
        trip_payouts_slab_os_rate: slabKmRate.toFixed(2), // fixed decimal
        trip_payouts_charge_amount: baseChargeAmount.toFixed(2),
        trip_payouts_hire_amount: totalHireAmount.toFixed(2),
        trip_payouts_no_of_os_batta: daysUsed.toString(),
        trip_payouts_os_batta_per_day: osDayBattaPerDay.toFixed(2),
        trip_payouts_os_batta_total_amount: osDayBattaTotal.toFixed(2),
        trip_payouts_no_of_os_night_batta: noOfOsNightBatta.toString(),
        trip_payouts_os_night_batta: osNightBattaRate.toFixed(2),
        trip_payouts_os_night_batta_total_amount: osNightBattaTotal.toFixed(2),
        trip_payouts_slab_hour: "0",
        trip_payouts_slab_rate: "0",
        trip_payouts_extra_hour: "0",
        trip_payouts_extra_kms: "0",
        trip_payouts_slab_extra_hour_rate: "0",
        trip_payouts_slab_extra_km_rate: "0",
      };
    },

    AirportTransfer: ({ values, tariff }) => {
      const slabHour = parseFloat(values.trip_payouts_slab_hour || 0);
      const slabKm = parseFloat(values.trip_payouts_slab_km || 0);
      const extraHour = parseFloat(values.trip_payouts_extra_hour || 0);
      const extraKms = parseFloat(values.trip_payouts_extra_kms || 0);

      // Calculate slab rate based on slabHour + extraHour and slabKm + extraKms proportions, or use flat rate from tariff
      // Since your requirement is slab_rate = charge_amount, we use tariff.purchase_airport_transfer_rate as base
      const baseRate = parseFloat(tariff?.purchase_airport_transfer_rate || 0);

      // You can implement further logic if you want to proportion rate on slabs + extras
      const slabRate = baseRate;

      // Use slabRate as charge amount
      const chargeAmount = slabRate;

      // Total hire amount same as chargeAmount (can add extras if needed)
      const hireAmount = chargeAmount;

      return {
        trip_payouts_hours_used: values.trip_payouts_hours_used || "0:00", // display only
        trip_payouts_kms_used:
          tripPayoutsKmsUsed?.toString() || values.trip_payouts_kms_used || "0",
        trip_payouts_slab_hour: slabHour.toString(),
        trip_payouts_slab_km: slabKm.toString(),
        trip_payouts_extra_hour: extraHour.toString(),
        trip_payouts_extra_kms: extraKms.toString(),
        trip_payouts_slab_rate: slabRate.toFixed(2),
        trip_payouts_charge_amount: chargeAmount.toFixed(2),
        trip_payouts_hire_amount: hireAmount.toFixed(2),
      };
    },

    EmployeeTransfer: ({ values, tariff, tripPayoutsKmsUsed }) => {
      const slabHour = parseFloat(values.trip_payouts_slab_hour || 0);
      const slabKm = parseFloat(values.trip_payouts_slab_km || 0);
      const extraHour = parseFloat(values.trip_payouts_extra_hour || 0);
      const extraKms = parseFloat(values.trip_payouts_extra_kms || 0);

      // Use employee transfer rate as base
      const baseRate = parseFloat(tariff?.purchase_employee_transfer_rate || 0);

      // You can add further logic to proportion rate if needed
      const slabRate = baseRate;

      const chargeAmount = slabRate;

      const hireAmount = chargeAmount;

      return {
        trip_payouts_hours_used: values.trip_payouts_hours_used || "0:00", // display only
        trip_payouts_kms_used:
          tripPayoutsKmsUsed?.toString() || values.trip_payouts_kms_used || "0",
        trip_payouts_slab_hour: slabHour.toString(),
        trip_payouts_slab_km: slabKm.toString(),
        trip_payouts_extra_hour: extraHour.toString(),
        trip_payouts_extra_kms: extraKms.toString(),
        trip_payouts_slab_rate: slabRate.toFixed(2),
        trip_payouts_charge_amount: chargeAmount.toFixed(2),
        trip_payouts_hire_amount: hireAmount.toFixed(2),
      };
    },

    Contract: ({ values, tripPayoutsKmsUsed }) => {
      // For Contract type: no calculations, only display UsedHours and UsedKms
      // Hide or clear other charge-related fields by returning empty strings or zeros

      return {
        trip_payouts_hours_used: values.trip_payouts_hours_used || "0:00",
        trip_payouts_kms_used:
          tripPayoutsKmsUsed?.toString() || values.trip_payouts_kms_used || "0",
        trip_payouts_slab_hour: "",
        trip_payouts_slab_km: "",
        trip_payouts_extra_hour: "",
        trip_payouts_extra_kms: "",
        trip_payouts_slab_rate: "",
        trip_payouts_charge_amount: "",
        trip_payouts_hire_amount: "",
      };
    },

    // dutyTypeStrategies.OSDayRate
    OSDayRate: ({ values, tariff }) => {
      if (!tariff) {
        return {
          trip_payouts_days_used: "0",
          trip_payouts_kms_used: "0",
          trip_payouts_slab_km: "0",
          trip_payouts_charge_amount: "0",
          trip_payouts_hire_amount: "0",
          trip_payouts_slab_os_rate: "0",
          trip_payouts_extra_kms: "0",
          trip_payouts_extra_km_amount: "0",
          trip_payouts_no_of_os_batta: "0",
          trip_payouts_os_batta_per_day: "0",
          trip_payouts_os_batta_total_amount: "0",
          trip_payouts_no_of_os_night_batta: "0",
          trip_payouts_os_night_batta: "0",
          trip_payouts_os_night_batta_total_amount: "0",
        };
      }

      const daysUsed = parseFloat(values.trip_payouts_days_used) || 1;
      const kmsUsed = parseFloat(values.trip_payouts_kms_used) || 0;
      const slabOsRate = parseFloat(tariff.purchase_os_day_rate) || 0;
      const minKmsPerDay = parseFloat(tariff.purchase_os_min_kms) || 0;
      const slabKms = minKmsPerDay * daysUsed;

      const slabExtraKmRate = parseFloat(tariff?.purchase_os_km_rate ?? 0);
      const baseChargeAmount = slabOsRate * daysUsed;

      const extraKms = Math.max(0, kmsUsed - slabKms);
      const extraKmAmount = extraKms * slabExtraKmRate;

      const noOfOsBatta =
        parseFloat(values.trip_payouts_no_of_os_batta) ||
        parseFloat(values.trip_payouts_days_used) ||
        1;

      const osBattaPerDay =
        parseFloat(values.trip_payouts_os_batta_per_day) ||
        parseFloat(tariff?.purchase_driver_batta ?? "0") ||
        0;

      const osBattaTotalAmount = noOfOsBatta * osBattaPerDay;

      const noOfOsNightBatta =
        parseFloat(values.trip_payouts_no_of_os_night_batta) || 0;

      const osNightBatta =
        parseFloat(values.trip_payouts_os_night_batta) ||
        parseFloat(tariff.purchase_night_batta) ||
        0;

      const osNightBattaTotalAmount = noOfOsNightBatta * osNightBatta;

      // Set these in your return/setValues

      const fuelEscRate =
        values.trip_payouts_fuel_esculation_yes_no === "Yes"
          ? parseFloat(tariff.purchase_fuel_escalation_per_km || 0)
          : 0;
      const fuelEscAmount = fuelEscRate * kmsUsed;

      const escortAmount =
        values.trip_payouts_escort_yes_no === "Yes"
          ? parseFloat(tariff.purchase_escort_amount || 0)
          : 0;

      const totalHireAmount =
        baseChargeAmount +
        extraKmAmount +
        osBattaTotalAmount +
        osNightBattaTotalAmount +
        fuelEscAmount +
        (parseFloat(values.trip_payouts_local_batta) || 0) +
        (parseFloat(values.trip_payouts_permit) || 0) +
        (parseFloat(values.trip_payouts_parking) || 0) +
        (parseFloat(values.trip_payouts_toll_fasttag) || 0) +
        (parseFloat(values.trip_payouts_others) || 0) +
        escortAmount -
        ((parseFloat(values.trip_payouts_advance) || 0) +
          (parseFloat(values.trip_payouts_booking_advance) || 0));

      return {
        trip_payouts_days_used: daysUsed.toString(),
        trip_payouts_kms_used: kmsUsed.toString(),
        trip_payouts_slab_os_rate: slabOsRate.toString(),
        trip_payouts_slab_km: slabKms.toString(),
        trip_payouts_charge_amount: baseChargeAmount.toFixed(2),
        trip_payouts_extra_kms: extraKms.toFixed(2),
        trip_payouts_extra_km_amount: extraKmAmount.toFixed(2),

        trip_payouts_no_of_os_batta: noOfOsBatta.toString(),
        trip_payouts_os_batta_per_day: osBattaPerDay.toFixed(2),
        trip_payouts_os_batta_total_amount: osBattaTotalAmount.toFixed(2),
        trip_payouts_no_of_os_night_batta: noOfOsNightBatta.toString(),
        trip_payouts_os_night_batta: osNightBatta.toFixed(2),
        trip_payouts_os_night_batta_total_amount:
          osNightBattaTotalAmount.toFixed(2),

        trip_payouts_fuel_esculation_rate: fuelEscRate.toFixed(2),
        trip_payouts_fuel_esculation_amount: fuelEscAmount.toFixed(2),
        trip_payouts_escort_amount: escortAmount.toFixed(2),
        trip_payouts_hire_amount: totalHireAmount.toFixed(2),
      };
    },

    // Placeholder for other DutyTypes
    default: () => ({}),
  };

  const inputStyle = {
    fontSize: 14,
    height: 26,
    color: "#1e293b",

    "& .MuiInputBase-input": {
      paddingTop: "2px",
      paddingBottom: "2px",
      fontSize: 14,
      fontWeight: 600,
      color: "#1e293b",
      WebkitTextFillColor: "#1e293b",
      height: 22,
      boxSizing: "border-box",
      textAlign: "right",
    },

    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#cbd5e1",
    },

    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#94a3b8",
    },
  };

  const commonInputStyle = {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    ...inputStyle,
  };

  const dropdownStyle = {
    width: "100%",
    maxWidth: "100%",
    fontSize: 14,
    fontWeight: 600,
    color: "#1e293b",
    height: 24,
    minWidth: 0,

    "& .MuiSelect-select": {
      paddingTop: 2,
      paddingBottom: 2,
      height: 16,
      display: "flex",
      alignItems: "center",
      boxSizing: "border-box",
      fontSize: 14,
      fontWeight: 600,
      color: "#1e293b",
    },

    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#cbd5e1",
    },

    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#94a3b8",
    },
  };

  const noRowHoverStyle = {
    "&:hover": {
      backgroundColor: "transparent",
    },
    "& .MuiInputBase-root:focus, & .MuiInputBase-root.Mui-focused": {
      backgroundColor: "inherit",
    },
  };

  const renderDropdown = (fieldName) => (
    <Select
      size="small"
      displayEmpty
      value={values[fieldName] || ""}
      onChange={(e) => handleChange(fieldName, e.target.value)}
      sx={dropdownStyle}
    >
      <MenuItem
        value=""
        sx={{ fontSize: 14, fontWeight: 500, color: "#1e293b" }}
      >
        <em>Select</em>
      </MenuItem>

      <MenuItem
        value="Yes"
        sx={{ fontSize: 14, fontWeight: 500, color: "#1e293b" }}
      >
        Yes
      </MenuItem>

      <MenuItem
        value="No"
        sx={{ fontSize: 14, fontWeight: 500, color: "#1e293b" }}
      >
        No
      </MenuItem>
    </Select>
  );

  return (
    <Grid container spacing={1} padding={1}>
      <Grid item xs={12}>
        <div
          style={{
            width: "100%",
            maxWidth: "100%",
            overflowX: "auto",
            boxSizing: "border-box",
            // paddingRight: "5px", // Right padding
            borderRadius: 8, // rounded corners
            border: "1px solid #ddd", // optional border for visibility
          }}
        >
          {/* <Table size="small" style={{ tableLayout: 'fixed' }}> */}
          <Table
            sx={{
              "& tr": {
                height: 32,
              },
              "& td, & th": {
                paddingTop: 0.2,
                paddingBottom: 0,
                fontSize: 13,
                lineHeight: 1.3,
                color: "#1e293b",
              },
              "& th": {
                fontWeight: 700,
                color: "#334155",
              },
              "& .MuiTableCell-root": {
                borderColor: "#cbd5e1",
              },
            }}
          >
            <TableHead>
              <TableRow sx={{ height: 36 }}>
                <TableCell
                  colSpan={5}
                  sx={{ fontWeight: "bold", fontSize: 16, padding: 1 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column", // stack items
                      gap: 0,
                    }}
                  >
                    {/* Row 1 - Label */}
                    <Typography
                      variant="body1"
                      sx={{
                        whiteSpace: "nowrap",
                        fontWeight: "bold",
                        fontSize: 14,
                        padding: 0.4,
                      }}
                    >
                      <span style={{ color: "black" }}>{displayLabel} </span>
                      <span
                        style={{
                          color: displayText ? "#1976d2" : "gray",
                          fontWeight: displayText ? "bold" : "normal",
                        }}
                      >
                        {displayText ||
                          `(No ${displayLabel
                            .toLowerCase()
                            .replace(":", "")} selected)`}
                      </span>
                    </Typography>

                    {/* Row 2 - Dropdowns */}
                    <Box
                      sx={{
                        height: 24,
                        display: "flex",
                        gap: 2,
                        flexWrap: "wrap",
                        marginBottom: 1,
                      }}
                    >
                      <FormControl
                        size="small"
                        sx={{ minWidth: 290, maxWidth: 290 }}
                      >
                        <Select
                          value={localDutyType || ""}
                          onChange={(e) => {
                            const newType = e.target.value;
                            setLocalDutyType(newType);
                            if (setSelectedDutyType) {
                              setSelectedDutyType(newType);
                            }
                            handleDutyFormChange("payouts", {
                              trip_payouts_duty: newType,
                            });
                          }}
                          sx={{
                            minHeight: 24,
                            "& .MuiSelect-select": {
                              paddingTop: "1px",
                              paddingBottom: "1px",
                            },
                          }}
                        >
                          <MenuItem sx={{ minHeight: 24 }} value="Local">
                            Local
                          </MenuItem>
                          <MenuItem sx={{ minHeight: 24 }} value="Outstation">
                            Outstation
                          </MenuItem>
                          <MenuItem
                            sx={{ minHeight: 24 }}
                            value="EmployeeTransfer"
                          >
                            Employee Transfer
                          </MenuItem>
                          <MenuItem
                            sx={{ minHeight: 24 }}
                            value="AirportTransfer"
                          >
                            Airport Transfer
                          </MenuItem>
                          <MenuItem sx={{ minHeight: 24 }} value="Contract">
                            Monthly Contract
                          </MenuItem>
                          <MenuItem sx={{ minHeight: 24 }} value="Package">
                            Package
                          </MenuItem>
                          <MenuItem sx={{ minHeight: 24 }} value="OSDayRate">
                            OS Day Rate
                          </MenuItem>
                          <MenuItem sx={{ minHeight: 24 }} value="KmSlab">
                            Km Slab
                          </MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl
                        size="small"
                        sx={{
                          minWidth: 290,
                          maxWidth: 290,
                          fontSize: 12,
                        }}
                      >
                        <Select
                          // value={selectedVehicleGroupId || ""}
                          // onChange={onVehicleGroupChange}
                          value={localVehicleGroupId}
                          onChange={(e) =>
                            handleVehicleGroupChange(e.target.value)
                          }
                          sx={{
                            fontSize: 14,
                            minHeight: 24,
                            "& .MuiSelect-select": {
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              paddingTop: "2px",
                              paddingBottom: "2px",
                            },
                          }}
                        >
                          {vehicleGroups.map((vg) => (
                            <MenuItem key={vg.categoryid} value={vg.categoryid}>
                              {vg.categoryname}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>

              <TableRow
                sx={{
                  backgroundColor: "#f5f5f5", // light gray background
                  borderBottom: "1px solid rgba(0, 0, 0, 0.12)", // subtle bottom border
                }}
              >
                <TableCell
                  sx={{
                    width: 100,
                    minWidth: 100,
                    borderRight: "1px solid rgba(0, 0, 0, 0.12)",
                    fontWeight: "bold",
                  }}
                >
                  Particulars
                </TableCell>
                <TableCell
                  sx={{
                    width: 100,
                    minWidth: 100,
                    borderRight: "1px solid rgba(0, 0, 0, 0.12)",
                    fontWeight: "bold",
                  }}
                >
                  Hours/Days
                </TableCell>
                <TableCell
                  sx={{
                    width: 100,
                    minWidth: 100,
                    borderRight: "1px solid rgba(0, 0, 0, 0.12)",
                    fontWeight: "bold",
                  }}
                >
                  Kms
                </TableCell>
                <TableCell
                  sx={{
                    width: 100,
                    minWidth: 100,
                    borderRight: "1px solid rgba(0, 0, 0, 0.12)",
                    fontWeight: "bold",
                  }}
                >
                  Rate
                </TableCell>
                <TableCell
                  sx={{
                    width: 100,
                    minWidth: 100,
                    borderRight: "1px solid rgba(0, 0, 0, 0.12)",
                    fontWeight: "bold",
                  }}
                >
                  Amount
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {/* All rows below with the same column width settings */}
              <TableRow sx={noRowHoverStyle}>
                <TableCell sx={{ width: 100, minWidth: 100 }}>Usages</TableCell>
                <TableCell sx={{ width: 100, minWidth: 100 }}>
                  {selectedDutyType === "Outstation" ||
                  selectedDutyType === "OSDayRate" ||
                  selectedDutyType === "Package"
                    ? renderField("trip_payouts_days_used", "Days Used")
                    : renderField(
                        "trip_payouts_hours_used",
                        "Hours Used",
                        hoursUsedDisplay,
                      )}
                </TableCell>

                <TableCell sx={{ width: 100, minWidth: 100 }}>
                  {renderField("trip_payouts_kms_used", "Kms Used")}
                </TableCell>
                <TableCell sx={{ width: 100, minWidth: 100 }} />
                <TableCell sx={{ width: 100, minWidth: 100 }} />
              </TableRow>

              {/* Payouts - hide for Contract */}
              {selectedDutyType !== "Contract" && (
                <TableRow sx={noRowHoverStyle}>
                  <TableCell sx={{ width: 100, minWidth: 100 }}>
                    Payouts
                  </TableCell>
                  {selectedDutyType === "Outstation" ||
                  selectedDutyType === "OSDayRate" ||
                  selectedDutyType === "EmployeeTransfer" ||
                  selectedDutyType === "Package" ? (
                    <TableCell sx={{ width: 100, minWidth: 100 }}>
                      {/* Dummy empty cell to maintain layout */}
                    </TableCell>
                  ) : (
                    <TableCell sx={{ width: 100, minWidth: 100 }}>
                      {renderField("trip_payouts_slab_hour", "Slab Hour")}
                    </TableCell>
                  )}

                  {selectedDutyType === "EmployeeTransfer" ? (
                    <TableCell sx={{ width: 100, minWidth: 100 }}>
                      {/* Empty dummy cell */}
                    </TableCell>
                  ) : (
                    <TableCell sx={{ width: 100, minWidth: 100 }}>
                      {renderField("trip_payouts_slab_km", "Slab Km")}
                    </TableCell>
                  )}

                  <TableCell>
                    {["Outstation", "OSDayRate", "Package"].includes(
                      selectedDutyType,
                    )
                      ? renderField("trip_payouts_slab_os_rate", "Slab OS Rate")
                      : renderField("trip_payouts_slab_rate", "Slab Rate")}
                  </TableCell>

                  <TableCell sx={{ width: 100, minWidth: 100 }}>
                    {renderField("trip_payouts_charge_amount", "Charge Amount")}
                  </TableCell>
                </TableRow>
              )}

              {/* Extra Hour and Local Batta rows hidden for OSDayRate */}
              {!(
                selectedDutyType === "Outstation" ||
                selectedDutyType === "OSDayRate" ||
                selectedDutyType === "Package" ||
                selectedDutyType === "EmployeeTransfer" ||
                selectedDutyType === "Contract"
              ) && (
                <>
                  {/* Extra Hour */}
                  <TableRow sx={noRowHoverStyle}>
                    <TableCell sx={{ width: 100, minWidth: 100 }}>
                      Extra Hour
                    </TableCell>
                    <TableCell sx={{ width: 100, minWidth: 100 }}>
                      {renderField("trip_payouts_extra_hour", "Extra Hour")}
                    </TableCell>
                    <TableCell sx={{ width: 100, minWidth: 100 }} />
                    <TableCell sx={{ width: 100, minWidth: 100 }}>
                      {renderField(
                        "trip_payouts_slab_extra_hour_rate",
                        "Slab Extra Hour Rate",
                      )}
                    </TableCell>
                    <TableCell sx={{ width: 100, minWidth: 100 }}>
                      {renderField(
                        "trip_payouts_extra_hour_amount",
                        "Extra Hour Amount",
                      )}
                    </TableCell>
                  </TableRow>
                </>
              )}

              {/* Show Extra Kms row only for OSDayRate, Local, and AirportTransfer */}
              {(selectedDutyType === "OSDayRate" ||
                selectedDutyType === "Local" ||
                selectedDutyType === "AirportTransfer") && (
                <TableRow sx={noRowHoverStyle}>
                  <TableCell sx={{ width: 100, minWidth: 100 }}>
                    Extra Kms
                  </TableCell>
                  <TableCell sx={{ width: 100, minWidth: 100 }} />
                  <TableCell sx={{ width: 100, minWidth: 100 }}>
                    {renderField("trip_payouts_extra_kms", "Extra Kms")}
                  </TableCell>
                  <TableCell sx={{ width: 100, minWidth: 100 }}>
                    {renderField(
                      "trip_payouts_slab_extra_km_rate",
                      "Slab Extra Km Rate",
                    )}
                  </TableCell>
                  <TableCell sx={{ width: 100, minWidth: 100 }}>
                    {renderField(
                      "trip_payouts_extra_km_amount",
                      "Extra Km Amount",
                    )}
                  </TableCell>
                </TableRow>
              )}

              {/* Local Batta - only for Local duty type */}
              {selectedDutyType === "Local" && (
                <TableRow sx={noRowHoverStyle}>
                  <TableCell sx={{ width: 100, minWidth: 100 }}>
                    Local Batta
                  </TableCell>
                  <TableCell sx={{ width: 100, minWidth: 100 }} />
                  <TableCell sx={{ width: 100, minWidth: 100 }} />
                  <TableCell sx={{ width: 100, minWidth: 100 }} />
                  <TableCell sx={{ width: 100, minWidth: 100 }}>
                    {renderField("trip_payouts_local_batta", "Local Batta")}
                  </TableCell>
                </TableRow>
              )}

              {/* Fuel Esculation */}
              <TableRow sx={noRowHoverStyle}>
                <TableCell sx={{ width: 100, minWidth: 100 }}>
                  Fuel Esculation
                </TableCell>
                <TableCell sx={{ width: 100, minWidth: 100 }} />
                <TableCell sx={{ width: 100, minWidth: 100 }}>
                  {renderDropdown("trip_payouts_fuel_esculation_yes_no")}
                </TableCell>
                <TableCell sx={{ width: 100, minWidth: 100 }}>
                  {renderField(
                    "trip_payouts_fuel_esculation_rate",
                    "Fuel Escalation Rate",
                  )}
                </TableCell>
                <TableCell sx={{ width: 100, minWidth: 100 }}>
                  {renderField(
                    "trip_payouts_fuel_esculation_amount",
                    "Fuel Escalation Amount",
                  )}
                </TableCell>
              </TableRow>

              {selectedDutyType !== "Local" &&
                selectedDutyType !== "AirportTransfer" &&
                selectedDutyType !== "EmployeeTransfer" &&
                selectedDutyType !== "Contract" && (
                  <>
                    {/* OS Day Batta */}
                    <TableRow sx={noRowHoverStyle}>
                      <TableCell sx={{ width: 100, minWidth: 100 }}>
                        OS Day Batta
                      </TableCell>
                      <TableCell sx={{ width: 100, minWidth: 100 }} />
                      <TableCell sx={{ width: 100, minWidth: 100 }}>
                        {renderField(
                          "trip_payouts_no_of_os_batta",
                          "No of OS Batta",
                        )}
                      </TableCell>
                      <TableCell sx={{ width: 100, minWidth: 100 }}>
                        {renderField(
                          "trip_payouts_os_batta_per_day",
                          "OS Batta per Day",
                        )}
                      </TableCell>
                      <TableCell sx={{ width: 100, minWidth: 100 }}>
                        {renderField(
                          "trip_payouts_os_batta_total_amount",
                          "OS Batta Total Amount",
                        )}
                      </TableCell>
                    </TableRow>

                    {/* OS Night Batta */}
                    <TableRow sx={noRowHoverStyle}>
                      <TableCell sx={{ width: 100, minWidth: 100 }}>
                        OS Night Batta
                      </TableCell>
                      <TableCell sx={{ width: 100, minWidth: 100 }} />
                      <TableCell sx={{ width: 100, minWidth: 100 }}>
                        {renderField(
                          "trip_payouts_no_of_os_night_batta",
                          "No of OS Night Batta",
                        )}
                      </TableCell>
                      <TableCell sx={{ width: 100, minWidth: 100 }}>
                        {renderField(
                          "trip_payouts_os_night_batta",
                          "OS Night Batta",
                        )}
                      </TableCell>
                      <TableCell sx={{ width: 100, minWidth: 100 }}>
                        {renderField(
                          "trip_payouts_os_night_batta_total_amount",
                          "OS Night Batta Total Amount",
                        )}
                      </TableCell>
                    </TableRow>

                    {/* Permit */}
                    <TableRow sx={noRowHoverStyle}>
                      <TableCell sx={{ width: 100, minWidth: 100 }}>
                        Permit
                      </TableCell>
                      <TableCell sx={{ width: 100, minWidth: 100 }} />
                      <TableCell sx={{ width: 100, minWidth: 100 }} />
                      <TableCell sx={{ width: 100, minWidth: 100 }} />
                      <TableCell sx={{ width: 100, minWidth: 100 }}>
                        {renderField("trip_payouts_permit", "Permit")}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              {/* Parking */}
              <TableRow sx={noRowHoverStyle}>
                <TableCell sx={{ width: 100, minWidth: 100 }}>
                  Parking
                </TableCell>
                <TableCell sx={{ width: 100, minWidth: 100 }} />
                <TableCell sx={{ width: 100, minWidth: 100 }} />
                <TableCell sx={{ width: 100, minWidth: 100 }} />
                <TableCell sx={{ width: 100, minWidth: 100 }}>
                  {renderField("trip_payouts_parking", "Parking")}
                </TableCell>
              </TableRow>

              {/* Toll/Fasttag */}
              <TableRow sx={noRowHoverStyle}>
                <TableCell sx={{ width: 100, minWidth: 100 }}>
                  Toll/Fasttag
                </TableCell>
                <TableCell sx={{ width: 100, minWidth: 100 }} />
                <TableCell sx={{ width: 100, minWidth: 100 }} />
                <TableCell sx={{ width: 100, minWidth: 100 }} />
                <TableCell sx={{ width: 100, minWidth: 100 }}>
                  {renderField("trip_payouts_toll_fasttag", "Toll/Fasttag")}
                </TableCell>
              </TableRow>

              {/* Others */}
              <TableRow sx={noRowHoverStyle}>
                <TableCell sx={{ width: 100, minWidth: 100 }}>Others</TableCell>
                <TableCell sx={{ width: 100, minWidth: 100 }} />
                <TableCell sx={{ width: 100, minWidth: 100 }} />
                <TableCell sx={{ width: 100, minWidth: 100 }} />
                <TableCell sx={{ width: 100, minWidth: 100 }}>
                  {renderField("trip_payouts_others", "Others")}
                </TableCell>
              </TableRow>

              {/* Escorts */}
              <TableRow sx={noRowHoverStyle}>
                <TableCell sx={{ width: 100, minWidth: 100 }}>
                  Escorts
                </TableCell>
                <TableCell sx={{ width: 100, minWidth: 100 }} />
                <TableCell sx={{ width: 100, minWidth: 100 }} />
                <TableCell sx={{ width: 100, minWidth: 100 }}>
                  {renderDropdown("trip_payouts_escort_yes_no")}
                </TableCell>
                <TableCell sx={{ width: 100, minWidth: 100 }}>
                  {renderField("trip_payouts_escort_amount", "Escort Amount")}
                </TableCell>
              </TableRow>

              {/* Advance - hide for Contract */}
              {selectedDutyType !== "Contract" && (
                <TableRow sx={noRowHoverStyle}>
                  <TableCell sx={{ width: 100, minWidth: 100 }}>
                    Advance
                  </TableCell>
                  <TableCell sx={{ width: 100, minWidth: 100 }} />
                  <TableCell sx={{ width: 100, minWidth: 100 }} />
                  <TableCell sx={{ width: 120, minHeight: 28 }}>
                    <TextField
                      size="small"
                      type="date"
                      value={
                        dutyForm.trip_payouts_advance_date
                          ? dutyForm.trip_payouts_advance_date.split("T")[0]
                          : ""
                      }
                      onChange={(e) =>
                        handleDutyFormChange(
                          "trip_payouts_advance_date",
                          e.target.value
                            ? new Date(e.target.value).toISOString()
                            : null,
                        )
                      }
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        width: 105,
                        minHeight: 28,
                        "& .MuiInputBase-root": { minHeight: 20 },
                        "& .MuiInputBase-input": {
                          fontSize: "0.80rem",
                          minHeight: 28,
                          padding: "0 4px",
                          lineHeight: 1.2,
                        },
                        "& label": {
                          fontSize: "0.85rem",
                          lineHeight: 1,
                          marginBottom: 0,
                        },
                      }}
                    />
                  </TableCell>

                  <TableCell sx={{ width: 100, minWidth: 100 }}>
                    {renderField("trip_payouts_advance", "Advance")}
                  </TableCell>
                </TableRow>
              )}

              {/* Booking Advance - hide for Contract */}
              {selectedDutyType !== "Contract" && (
                <TableRow sx={noRowHoverStyle}>
                  <TableCell sx={{ width: 100, minWidth: 100 }}>
                    Booking Advance
                  </TableCell>
                  <TableCell sx={{ width: 100, minWidth: 100 }} />
                  <TableCell sx={{ width: 100, minWidth: 100 }} />
                  <TableCell sx={{ width: 100, minWidth: 100 }} />
                  <TableCell sx={{ width: 100, minWidth: 100 }}>
                    {renderField(
                      "trip_payouts_booking_advance",
                      "Booking Advance",
                    )}
                  </TableCell>
                </TableRow>
              )}

              {/* Total */}
              <TableRow sx={noRowHoverStyle}>
                <TableCell
                  sx={{ width: 100, minWidth: 100, fontWeight: "bold" }}
                >
                  Total
                </TableCell>

                <TableCell sx={{ width: 100, minWidth: 100 }} />
                <TableCell sx={{ width: 100, minWidth: 100 }} />
                <TableCell sx={{ width: 100, minWidth: 100 }} />

                <TableCell
                  sx={{ width: 100, minWidth: 100, fontWeight: "bold" }}
                >
                  {renderField("trip_payouts_hire_amount", "Hire Amount")}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </Grid>
    </Grid>
  );
}
